// src/hooks/useAttribution.js
// ─────────────────────────────────────────────────────────────────────────────
// Captures UTM params, affiliate codes, and referral data on app load.
// Works in two modes:
//   1. URL params   — user clicked a tagged link from the marketing site
//   2. Cookie       — user navigated directly; marketing site set _rh_attr cookie
//
// Attribution is stored in localStorage so it survives page refreshes.
// Once the user signs up, call saveAttributionToProfile(userId) to persist
// it permanently on their profile row.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

const TRACKED_PARAMS = [
  // UTM standard
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  // Affiliate / referral
  'ref', 'affiliate', 'aff', 'partner', 'via',
  // Ad platform click IDs (for attribution reporting)
  'fbclid', 'gclid', 'ttclid', 'msclkid',
  // Promo / invite
  'coupon', 'promo', 'invite',
];

const STORAGE_KEY = '_rh_attr';
const COOKIE_NAME = '_rh_attr';

// ── Read the cross-domain cookie set by revhapi.com ──────────────────────────
function readAttributionCookie() {
  try {
    const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
    if (match) return JSON.parse(decodeURIComponent(match[1]));
  } catch (_) {}
  return null;
}

// ── Read attribution persisted in localStorage (survives refreshes) ──────────
export function getStoredAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

// ── Persist attribution to localStorage ──────────────────────────────────────
function storeAttribution(attrs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(attrs)); } catch (_) {}
}

// ── Save attribution to the user's Supabase profile (call after signup) ──────
export async function saveAttributionToProfile(userId) {
  if (!userId) return;

  const attrs = getStoredAttribution();
  if (!attrs || Object.keys(attrs).length === 0) return;

  try {
    await supabase
      .from('profiles')
      .update({
        attribution:   attrs,
        attributed_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .is('attribution', null); // only write once — don't overwrite existing attribution

    // Clean up localStorage after saving to DB
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  } catch (e) {
    console.warn('[Attribution] Failed to save to profile:', e.message);
  }
}

// ── Main hook — call once in App.jsx ─────────────────────────────────────────
export function useAttribution() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const attrs = {};

    // Priority 1: URL params (user clicked a tagged link right now)
    TRACKED_PARAMS.forEach(key => {
      if (params.has(key)) attrs[key] = params.get(key);
    });

    // Priority 2: cross-domain cookie from revhapi.com
    if (Object.keys(attrs).length === 0) {
      const cookie = readAttributionCookie();
      if (cookie) Object.assign(attrs, cookie);
    }

    // Priority 3: already in localStorage from a previous visit to the app
    if (Object.keys(attrs).length === 0) {
      const stored = getStoredAttribution();
      if (stored) return; // already saved — nothing new to do
    }

    if (Object.keys(attrs).length === 0) return;

    // Persist to localStorage for this session
    storeAttribution(attrs);

    // Also mirror the ref code into the existing revhapi_ref key
    // so Register.jsx's affiliate referral logic still works
    if (attrs.ref) {
      try { localStorage.setItem('revhapi_ref', attrs.ref.toUpperCase().trim()); } catch (_) {}
    }
  }, []); // runs once on mount
}

export default useAttribution;
